import { Box, Tooltip, Typography } from "@mui/material";

interface CellRemark {
	label?: string;
	value: string | null | undefined;
}

export interface CellWithRemarkIndicatorProps {
	value: string | null | undefined;
	remark?: string | null | undefined | CellRemark[];
}

interface VisibleRemark {
	label?: string;
	value: string;
}

export const CellWithRemarkIndicator = ({ value, remark }: CellWithRemarkIndicatorProps) => {
	const visibleRemarks = normalizeRemarks(remark).filter(isVisibleRemark);
	const hasRemark = visibleRemarks.length > 0;

	return (
		<Box
			sx={{
				position: "relative",
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{value ?? ""}
			</Box>
			{hasRemark && (
				<Tooltip title={<RemarkTooltipTitle remarks={visibleRemarks} />} arrow placement="top">
					<Box
						sx={{
							position: "absolute",
							top: 0,
							right: 0,
							width: 0,
							height: 0,
							borderStyle: "solid",
							borderWidth: "0 12px 12px 0",
							borderColor: "transparent #ef9a9a transparent transparent",
							cursor: "pointer",
						}}
					/>
				</Tooltip>
			)}
		</Box>
	);
};

function normalizeRemarks(remark: CellWithRemarkIndicatorProps["remark"]): CellRemark[] {
	if (Array.isArray(remark)) return remark;
	return [{ value: remark }];
}

function isVisibleRemark(remark: CellRemark): remark is VisibleRemark {
	return remark.value != null && remark.value.length > 0;
}

interface RemarkTooltipTitleProps {
	remarks: VisibleRemark[];
}

const RemarkTooltipTitle = ({ remarks }: RemarkTooltipTitleProps) => {
	const firstRemark = remarks[0];
	if (remarks.length === 1 && firstRemark != null && firstRemark.label == null) return firstRemark.value;

	return (
		<Box sx={{ display: "grid", gap: 1 }}>
			{remarks.map((remark) => (
				<Box key={remark.label ?? remark.value}>
					{remark.label != null && (
						<Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
							{remark.label}
						</Typography>
					)}
					<Typography variant="body2">{remark.value}</Typography>
				</Box>
			))}
		</Box>
	);
};
